"use client";

import * as React from "react";
import "react-phone-input-2/lib/style.css";
import { cn } from "../utils";
import { RequiredMark } from "../required-mark";

/** Subset of react-phone-input-2 props we rely on. */
interface PhoneInputProps {
  country?: string;
  value?: string;
  onChange?: (value: string, data?: {
    dialCode?: string;
  }) => void;
  onBlur?: () => void;
  disabled?: boolean;
  placeholder?: string;
  specialLabel?: string;
  masks?: Record<string, string>;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  inputClass?: string;
  buttonClass?: string;
  dropdownClass?: string;
  searchClass?: string;
  enableSearch?: boolean;
  countryCodeEditable?: boolean;
}
export interface InputPhoneNumberProps {
  /** Field name (also used for the input id when `id` is not given) */
  name: string;
  /** Explicit id override */
  id?: string;
  /** Label rendered above the field */
  label?: string | null;
  /** Helper text rendered below the field — plain string or rich content (e.g. a description with a link) */
  description?: React.ReactNode;
  /** Placeholder text */
  placeholder?: string;
  /** Current value (E.164-ish, digits with dial code) */
  value?: string;
  /** Fires with the raw value string */
  onChange?: (value: string) => void;
  /** Fires when the field loses focus */
  onBlur?: () => void;
  /** Marks the field required (renders the asterisk) */
  required?: boolean;
  /** Error message — replaces the helper text and applies error styling */
  error?: string;
  /** Default country (ISO2, lowercase). Defaults to South Africa. */
  country?: string;
  /** Enable the searchable country list */
  enableSearch?: boolean;
  /** Disable the field */
  disabled?: boolean;
  /** Extra classes for the outer container */
  className?: string;
}

/**
 * InputPhoneNumber
 *
 * V&A Waterfront Design System — phone field with a country-prefix dropdown.
 * Wraps `react-phone-input-2` and restyles its internals to match the rest of
 * the form inputs (see `FormInput` / `InputDate`): `h-12`, `rounded-sm`,
 * `bg-input-surface-rest`, transparent border, and the shared label / helper /
 * error structure. The library is imported lazily to stay SSR-safe.
 */
export const InputPhoneNumber = React.forwardRef<HTMLDivElement, InputPhoneNumberProps>(({
  name,
  id,
  label,
  description,
  placeholder,
  value,
  onChange,
  onBlur,
  required,
  error,
  country = "za",
  enableSearch,
  disabled,
  className
}, ref) => {
  const generatedId = React.useId();
  const inputId = id || name || generatedId;
  const descriptionId = `${inputId}-description`;
  const hasError = Boolean(error);
  const hasDescription = Boolean(error || description);

  // Current country dial code (e.g. "27"), captured from react-phone-input-2's
  // onChange so we can strip the national trunk prefix on blur.
  const dialCodeRef = React.useRef("");
  const [PhoneInput, setPhoneInput] = React.useState<React.ComponentType<PhoneInputProps> | null>(null);
  React.useEffect(() => {
    let active = true;
    // Lazy import keeps react-phone-input-2 (which touches `window`) off the server.
    import("react-phone-input-2").then(mod => {
      const Component = (mod.default || mod) as React.ComponentType<PhoneInputProps>;
      if (active) setPhoneInput(() => Component);
    });
    return () => {
      active = false;
    };
  }, []);
  return <div ref={ref} data-testid="input-phone-number" className={cn("group/input-phone flex w-full flex-col gap-2",
  // Flag / prefix button — blend into the input surface
  "[&_.flag-dropdown]:!border-0 [&_.flag-dropdown]:!bg-transparent", "[&_.selected-flag]:!bg-transparent [&_.selected-flag.open]:!bg-transparent [&_.selected-flag:hover]:!bg-transparent [&_.selected-flag:focus]:!bg-transparent",
  // Country dropdown — match the Select dropdown
  "[&_.country-list]:!m-0 [&_.country-list]:!mt-1 [&_.country-list]:!rounded-sm [&_.country-list]:!border [&_.country-list]:!border-border-subtle [&_.country-list]:!bg-surface-base-primary [&_.country-list]:!shadow-lg", "[&_.country-list_.country]:!px-4 [&_.country-list_.country]:!py-2.5 [&_.country-list_.country.highlight]:!bg-input-surface-hover [&_.country-list_.country:hover]:!bg-input-surface-hover", "[&_.country-list_.country-name]:!text-text-primary [&_.country-list_.dial-code]:!text-text-secondary", "[&_.country-list_.search]:!px-4 [&_.country-list_.search]:!py-2 [&_.country-list_.search-box]:!rounded-sm", className)}>
        {Boolean(label) && <label htmlFor={inputId} className={cn("typography-component-sm-medium desktop:typography-component-md-medium", disabled ? "text-input-text-disabled" : "text-input-text-label")}>
            {label}
            <RequiredMark required={required} />
          </label>}

        {PhoneInput ? <PhoneInput country={country}
    // react-phone-input-2 works in bare digits (dial code + national
    // number, no "+"); strip any leading "+" we emit before handing
    // the value back in.
    value={value?.replace(/^\+/, "")} onChange={(next, data) => {
      if (data?.dialCode) dialCodeRef.current = data.dialCode;
      // Re-add the "+" so the emitted value is real E.164 — this is
      // what lets libphonenumber-js infer the selected country
      // instead of assuming a hardcoded default (VAW2-223).
      onChange?.(next ? `+${next}` : "");
    }} onBlur={() => {
      // Drop the national trunk prefix (the locally-dialled leading 0)
      // once the user leaves the field, regardless of overall validity.
      const dc = dialCodeRef.current;
      const digits = value?.replace(/^\+/, "") ?? "";
      if (digits && dc && digits.startsWith(dc)) {
        const national = digits.slice(dc.length);
        if (national.startsWith("0")) {
          onChange?.(`+${dc}${national.replace(/^0/, "")}`);
        }
      }
      onBlur?.();
    }} disabled={disabled} enableSearch={enableSearch} specialLabel="" placeholder={placeholder} inputProps={{
      name,
      id: inputId,
      required,
      "aria-required": required || undefined,
      "aria-invalid": hasError || undefined,
      "aria-describedby": hasDescription ? descriptionId : undefined
    }} inputClass={cn("!h-12 !w-full !rounded-sm !border !border-transparent !bg-input-surface-rest !py-3 !pl-14 !pr-4", "!typography-body-sm-regular !text-input-text-rest", "!outline-none !transition-colors hover:!bg-input-surface-hover focus:!border-border-subtle focus:!bg-input-surface-hover", hasError && "!bg-input-surface-error hover:!bg-input-surface-error focus:!bg-input-surface-error", disabled && "!pointer-events-none !bg-input-surface-disabled !text-input-text-disabled")} buttonClass="!border-0 !bg-transparent" /> :
    // Placeholder to avoid layout shift before the lazy chunk loads
    <div className="h-12 w-full rounded-sm bg-input-surface-rest" aria-hidden />}

        {hasDescription && <p id={descriptionId} role={hasError ? "alert" : undefined} className={cn("typography-component-sm-regular desktop:typography-component-md-regular", hasError ? "text-input-text-error" : "text-input-text-label")}>
            {error || description}
          </p>}
      </div>;
});
InputPhoneNumber.displayName = "InputPhoneNumber";